/**
 * API Response Transformers
 * Phase 6: Frontend Application
 *
 * Transforms API responses (snake_case) to frontend types (camelCase).
 * Centralises the mapping between API DTOs and frontend domain types.
 *
 * @see MANDATORY_RULES.md Section 0.2 (British English)
 */

import type { Ingredient, InstructionStep, NutritionData, Recipe } from '@/types/recipe';
import { logger } from './logger';

/**
 * API Recipe response shape (snake_case from Prisma)
 * Note: Backend parses JSON fields before returning (Rule 0.17),
 * so these may be arrays/objects rather than strings
 */
export interface ApiRecipe {
  id: string;
  tenant_id: string;
  version: number;
  status: string;
  title: string;
  description: string | null;
  servings_count: number;
  servings_unit: string;
  prep_time_minutes: number;
  cook_time_minutes: number;
  total_time_minutes: number;
  ingredients_raw: string | string[]; // JSON string or parsed array
  instructions_raw: string | string[]; // JSON string or parsed array
  ingredients_normalized: string | unknown[] | null; // JSON string or parsed array
  instructions_canonical: string | unknown[] | null; // JSON string or parsed array
  difficulty: string | null;
  diet_tags: string | string[] | null; // JSON string or parsed array
  taxonomy_tags: string | string[] | null; // JSON string or parsed array
  health_tags: string | string[] | null; // JSON string or parsed array - health benefit tags
  meal_types: string | string[] | null; // JSON string or parsed array - meal type categorisation
  equipment: string | string[] | null; // JSON string or parsed array
  nutrition_data: string | unknown | null; // JSON string or parsed object
  // Pre-transformed nutrition from backend. perServing or per100g can be null
  // for drafts (e.g. when serving_size_g is missing/0 — backend cannot compute
  // per100g without it).
  nutrition: {
    perServing: Record<string, unknown> | null;
    per100g: Record<string, unknown> | null;
  } | null;
  allergy_flags: string | unknown | null; // JSON string or parsed object
  gut_health_score: number | null; // 0-10 score for gut health
  nutrition_score: string | null;   // UK FSA grade A-E
  simplicity_score: number | null;  // 1-5 stars (5 = simplest)
  image_url: string | null;
  image_thumbnail_url: string | null;
  source_type: string | null;
  source_url: string | null;
  source_author: string | null;
  enrichment_status: string;
  enrichment_attempts: number;
  enrichment_confidence: string | unknown | null; // JSON string or parsed object
  created_by: string;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  published_by: string | null;
  deleted_at: string | null;
}

/**
 * API list response wrapper
 */
export interface ApiListResponse<T> {
  data: T[];
  pagination: {
    cursor: string | null;
    has_more: boolean;
  };
  meta: {
    timestamp: string;
  };
}

/**
 * API single item response wrapper
 */
export interface ApiItemResponse<T> {
  data: T;
  meta: {
    timestamp: string;
  };
}

/**
 * Safely parse JSON string or return already-parsed data
 * Backend now returns parsed arrays/objects (Rule 0.17), but we handle both for compatibility
 */
function safeJsonParse<T>(json: string | T | null | undefined): T | null {
  if (!json) return null;
  
  // If already parsed (object or array), return as-is
  if (typeof json === 'object') {
    return json as T;
  }
  
  // If string, try to parse
  if (typeof json === 'string') {
    try {
      return JSON.parse(json) as T;
    } catch {
      logger.warn('Failed to parse JSON string:', json);
      return null;
    }
  }
  
  return null;
}

/**
 * Transform API status to frontend status type
 */
function mapStatus(apiStatus: string): Recipe['status'] {
  const statusMap: Record<string, Recipe['status']> = {
    draft: 'draft',
    pending_enrichment: 'pending_enrichment',
    pending_review: 'draft',  // Removed status — treat stale records as draft
    approved: 'published',    // Removed status — treat as published
    published: 'published',
    rejected: 'rejected',
    archived: 'archived',
  };
  return statusMap[apiStatus] ?? 'draft';
}

/**
 * Transform API difficulty to frontend difficulty type
 */
function mapDifficulty(apiDifficulty: string | null): Recipe['difficulty'] {
  if (!apiDifficulty) return 'medium';
  const difficultyMap: Record<string, Recipe['difficulty']> = {
    easy: 'easy',
    medium: 'medium',
    hard: 'hard',
    expert: 'hard', // Map expert to hard for UI
  };
  return difficultyMap[apiDifficulty] ?? 'medium';
}

/**
 * Transform raw ingredients array to frontend Ingredient type
 */
function transformIngredients(
  rawIngredients: string | string[] | null,
  normalizedIngredients: string | unknown[] | null
): Ingredient[] {
  // Prefer normalised if available
  const normalized = safeJsonParse<Array<{
    name: string;
    quantity: number | null;
    unit: string | null;
    preparation: string | null;
    optional: boolean;
  }>>(normalizedIngredients as string | null);

  if (normalized && normalized.length > 0) {
    return normalized.map((ing, index) => ({
      id: `ing-${index}`,
      name: ing.name,
      quantity: ing.quantity ?? 0,
      unit: ing.unit ?? '',
      preparation: ing.preparation ?? undefined,
      optional: ing.optional,
    }));
  }

  // Fallback to raw - parse "quantity unit name (preparation)" format
  const raw = safeJsonParse<string[]>(rawIngredients);
  if (!raw) return [];

  return raw.map((text, index) => {
    // Try to parse format: "quantity unit name (preparation)"
    // Example: "100 g Plain flour" or "2 Medium eggs" or "40 g Onion (diced)"
    const match = text.match(/^(\d+\.?\d*)\s*([a-zA-Z]*)\s+(.+?)(\s*\((.+)\))?$/);
    
    if (match) {
      const quantity = match[1] || '0';
      const unit = match[2] || '';
      const name = match[3] || text;
      const preparation = match[5];
      
      return {
        id: `ing-${index}`,
        name: name.trim(),
        quantity: parseFloat(quantity),
        unit: unit.trim(),
        preparation: preparation?.trim(),
        optional: false,
      };
    }
    
    // If no quantity/unit pattern, treat entire string as name
    return {
      id: `ing-${index}`,
      name: text.trim(),
      quantity: 0,
      unit: '',
      optional: false,
    };
  });
}

/**
 * Transform raw/canonical instructions to frontend InstructionStep type
 */
function transformInstructions(
  rawInstructions: string | string[] | null,
  canonicalInstructions: string | unknown[] | null
): InstructionStep[] {
  // Prefer canonical if available
  // Canonical format from LLM enrichment uses snake_case: {step_number, instruction, duration_minutes, ...}
  // Must map to frontend InstructionStep shape to avoid silent Zod validation failure on form submit
  const canonical = safeJsonParse<Array<Record<string, unknown>>>(canonicalInstructions as string | null);

  if (canonical && canonical.length > 0) {
    return canonical.map((inst, index) => ({
      stepNumber: (typeof inst['step_number'] === 'number' ? inst['step_number'] : null)
        ?? (typeof inst['stepNumber'] === 'number' ? inst['stepNumber'] : null)
        ?? (index + 1),
      instruction: (typeof inst['instruction'] === 'string' ? inst['instruction'] : null)
        ?? (typeof inst['action'] === 'string' ? inst['action'] : null)
        ?? '',
      timing: typeof inst['duration_minutes'] === 'number' && inst['duration_minutes'] > 0
        ? `${inst['duration_minutes']} minutes`
        : typeof inst['timing'] === 'string'
        ? inst['timing']
        : undefined,
      tips: Array.isArray(inst['tips']) ? (inst['tips'] as string[]) : undefined,
    }));
  }

  // Fallback to raw
  const raw = safeJsonParse<string[]>(rawInstructions);
  if (!raw) return [];

  return raw.map((text, index) => ({
    stepNumber: index + 1,
    instruction: text,
  }));
}

/**
 * Transform nutrition data from API format
 */
function transformNutrition(nutritionData: string | unknown | null): Recipe['nutrition'] | undefined {
  const data = safeJsonParse<{
    perServing?: NutritionData;
    per100g?: NutritionData;
  }>(nutritionData as string | null);

  if (!data) return undefined;

  // Ensure both are present
  if (!data.perServing || !data.per100g) return undefined;

  return {
    perServing: data.perServing,
    per100g: data.per100g,
  };
}

/**
 * Transform enrichment confidence to LLM enrichment data
 */
function transformEnrichment(
  enrichmentConfidence: string | unknown | null
): Recipe['llmEnrichment'] | undefined {
  const confidence = safeJsonParse<{
    tips?: string[];
    variations?: string[];
    pairings?: string[];
    history?: string;
  }>(enrichmentConfidence as string | null);

  if (!confidence) return undefined;

  return {
    tips: confidence.tips,
    variations: confidence.variations,
    pairings: confidence.pairings,
    history: confidence.history,
  };
}

/**
 * Create slug from title
 */
function createSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Transform single API recipe to frontend Recipe type
 */
export function transformRecipe(apiRecipe: ApiRecipe): Recipe {
  const dietTags = safeJsonParse<string[]>(apiRecipe.diet_tags) ?? [];
  const taxonomyTags = safeJsonParse<string[]>(apiRecipe.taxonomy_tags) ?? [];
  const healthTags = safeJsonParse<string[]>(apiRecipe.health_tags) ?? [];
  const mealTypes = safeJsonParse<string[]>(apiRecipe.meal_types) ?? [];

  // Prefer pre-transformed nutrition from backend, fallback to parsing nutrition_data.
  // Backend's transformNutrition can return per100g=null when serving_size_g is
  // missing/0 (common for drafts). The Recipe type guarantees both sides non-null,
  // so drop the whole block if either is missing — NutritionPanel is rendered
  // behind a truthiness check.
  const nutrition =
    apiRecipe.nutrition && apiRecipe.nutrition.perServing && apiRecipe.nutrition.per100g
      ? {
          perServing: apiRecipe.nutrition.perServing as unknown as NutritionData,
          per100g: apiRecipe.nutrition.per100g as unknown as NutritionData,
        }
      : transformNutrition(apiRecipe.nutrition_data);

  return {
    id: apiRecipe.id,
    title: apiRecipe.title,
    slug: createSlug(apiRecipe.title),
    description: apiRecipe.description ?? '',
    servings: {
      quantity: apiRecipe.servings_count,
      unit: apiRecipe.servings_unit,
    },
    timing: {
      prep: apiRecipe.prep_time_minutes,
      cook: apiRecipe.cook_time_minutes,
      total: apiRecipe.total_time_minutes,
    },
    difficulty: mapDifficulty(apiRecipe.difficulty),
    ingredients: transformIngredients(
      apiRecipe.ingredients_raw,
      apiRecipe.ingredients_normalized
    ),
    instructions: transformInstructions(
      apiRecipe.instructions_raw,
      apiRecipe.instructions_canonical
    ),
    nutrition,
    images: {
      hero: apiRecipe.image_url ?? undefined,
      gallery: [], // Not yet implemented
    },
    tags: taxonomyTags,
    dietary: dietTags,
    health_tags: healthTags,
    meal_types: mealTypes as Recipe['meal_types'],
    allergy_flags: safeJsonParse<Record<string, boolean>>(apiRecipe.allergy_flags as string | null) ?? undefined,
    gut_health_score: apiRecipe.gut_health_score ?? undefined,
    nutrition_score: apiRecipe.nutrition_score ?? undefined,
    simplicity_score: apiRecipe.simplicity_score ?? undefined,
    source_url: apiRecipe.source_url ?? undefined,
    source_author: apiRecipe.source_author ?? undefined,
    source_type: (apiRecipe.source_type as Recipe['source_type']) ?? undefined,
    enrichment_status: apiRecipe.enrichment_status,
    status: mapStatus(apiRecipe.status),
    llmEnrichment: transformEnrichment(apiRecipe.enrichment_confidence),
    createdAt: apiRecipe.created_at,
    created_by: apiRecipe.created_by,
    updatedAt: apiRecipe.updated_at,
    publishedAt: apiRecipe.published_at ?? undefined,
  };
}

/**
 * Transform array of API recipes
 */
export function transformRecipes(apiRecipes: ApiRecipe[]): Recipe[] {
  return apiRecipes.map(transformRecipe);
}

/**
 * Transform frontend Recipe input to API create/update format
 */
export function transformRecipeInput(input: {
  title: string;
  description?: string;
  servings?: { quantity: number; unit: string };
  timing?: { prep: number; cook: number };
  difficulty?: string;
  ingredients?: Array<{ name: string; quantity?: number; unit?: string }>;
  instructions?: Array<{ instruction: string }>;
  dietary?: string[];
  tags?: string[];
}): Record<string, unknown> {
  return {
    title: input.title,
    description: input.description,
    servings: input.servings
      ? {
          count: input.servings.quantity,
          unit: input.servings.unit,
        }
      : undefined,
    prep_time_minutes: input.timing?.prep,
    cook_time_minutes: input.timing?.cook,
    difficulty: input.difficulty,
    ingredients_raw: input.ingredients?.map((i) => {
      const parts = [];
      if (i.quantity) parts.push(String(i.quantity));
      if (i.unit) parts.push(i.unit);
      parts.push(i.name);
      return parts.join(' ');
    }),
    instructions_raw: input.instructions?.map((i) => i.instruction),
    diet_tags: input.dietary,
    taxonomy_tags: input.tags,
  };
}
